import { getVendorContent } from '@studio/design-system/themes/vendor.js';
// Constants for the first-run gate (ENG-3796). Only what the shipped screens
// actually read — this file grows with them rather than arriving full of
// values nothing references yet.

/** "Learn more →" on the bridge-concept screen. */
export const BRIDGE_HELP_ARTICLE_URL = () =>
  getVendorContent({
    iris: 'https://intercom.help/iris-ai/en/articles/14289251-what-is-the-iris-bridge',
    ptzoptics: 'https://hivehelp.ptzoptics.com/en/articles/15179741-what-is-the-hive-bridge'
  });

/**
 * How long to wait before moving focus to a screen's primary action.
 *
 * Focusing immediately on mount fights the entrance animation and, with a
 * screen reader running, talks over the heading the user has not heard yet.
 * The delay lets the title be announced first, then hands focus to the button.
 */
export const AUTO_FOCUS_DELAY_MS = 500;

/**
 * How long the connecting screen waits for a usable bridge before showing the
 * failure state.
 *
 * The clock starts when the funnel REACHES that screen, not on mount: a user
 * who spends thirty seconds reading the welcome and bridge-concept screens
 * must not arrive at a connection that has already timed out. It stops the
 * moment the bridge is ready.
 *
 * AutoLinkGuard has its own separate safety timeout. This budget covers only
 * the gap between "AutoLinkGuard released the app" and "a broker proxy and a
 * room are actually usable".
 *
 * 45s, raised from 30s on 2026-08-25 after watching a real cold boot fail the
 * ceiling and then connect seconds later. 30s was never measured — it came from
 * a house "<=30s for user-facing waits" habit — and it is also the SAME budget
 * the identity store gives an early "unlinked" answer before it settles, so the
 * two were racing and this screen could lose. A wait that ends in a false
 * failure is worse than a wait that runs a little longer.
 */
export const BRIDGE_READY_TIMEOUT_MS = 45_000;

/**
 * How long the connecting screen stays up at minimum, even when the Bridge is
 * already connected.
 *
 * A returning machine usually has a healthy Bridge before the user presses "Set
 * up your Bridge", so all three signals are true on arrival and the screen would
 * be skipped entirely. That reads as "nothing happened" and leaves the user
 * unsure anything was checked — and it means the screen is only ever SEEN when
 * something is wrong, which is the worst time for it to be unfamiliar.
 *
 * The rows show whatever is actually true for that moment: all green when the
 * Bridge is already up. There is deliberately no fake progress animation.
 *
 * Counted from the moment ALL THREE rows are green, not from arrival on the
 * screen. Counting from arrival meant a bridge that became ready at 1.1s ticked
 * the rows and left in the same instant, so the user never saw the green they
 * waited for (reported live 2026-08-25).
 */
export const MIN_DETECTING_DWELL_MS = 1_000;

/**
 * How long the bridge gets to accept a device before the add is called failed.
 *
 * This bounds the ADD, not the camera: it is the window for the bridge to
 * resolve a config, which it does in well under a second on a healthy agent.
 */
export const ADD_DEVICE_TIMEOUT_MS = 15_000;

/**
 * How long the "all checks passed" state stays on screen before advancing.
 *
 * Long enough to read three green ticks, short enough not to feel like a stall.
 */
export const CONNECTION_SUCCESS_DWELL_MS = 1_500;

/**
 * How long a silent control channel is given before the control row fails.
 *
 * Generous on purpose. Control resolves after video on many cameras, and a
 * premature cross on a camera that was about to answer is worse than a slow
 * verdict. What it must NOT do is never resolve — see connection-checks.ts for
 * why silence used to read as success.
 */
export const CONTROL_CHECK_TIMEOUT_MS = 30_000;

/**
 * How long the hidden probe waits for a real decoded frame.
 *
 * A camera that connects but sends nothing (wrong stream path, wrong codec)
 * fails here rather than hanging the screen.
 */
export const STREAM_DECODE_TIMEOUT_MS = 10_000;

/** How often Magic Moment samples its viewer for advancing frames. */
export const MAGIC_MOMENT_POLL_MS = 1_000;

/**
 * How long the magic moment waits for a decoded frame before showing its
 * no-video state.
 *
 * Long on purpose. A user watching their own camera come up is not waiting on
 * us, and cutting them off early to declare failure is worse than letting a
 * slow camera finish. The window ends in an honest failure panel, never in an
 * auto-advance to "You're all set" over a spinner (observed live 2026-08-06).
 */
export const MAGIC_MOMENT_FALLBACK_MS = 120_000;
